import { Drawer } from '@/components/ui/drawer';
import { EventDetails } from '@/features/events/components/event-details';
import InventoryDetails from '@/features/inventory/components/inventory-details';
import { NoteDetails } from '@/features/notes/components/note-details';
import { QuoteOrderDetailPage } from '@/features/quote-order-detail/pages/quote-order-detail-page';
import { TaskDetails } from '@/features/tasks/components/task-details';
import { useMemoDeepEqualValue } from '@/lib/hooks/use-memo-deep-equal';
import { useQueryParams } from '@/lib/hooks/use-query-params';
import { useReferrer } from '@/lib/hooks/use-referrer';
import { enumParam, numberParam, queryParamsConfig, stringParam } from '@/lib/query-params';
import { SrSalesOrderType } from '@plato/data/starrocks/types';
import type { ReactNode } from 'react';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { DrawersContextType } from './drawers-context';
import { DrawersContext } from './drawers-context';

export type AllDrawersProps = {
    canAddToDraft?: boolean;
};

const drawerQueryParams = queryParamsConfig({
    d_depth: numberParam(),
    d_product_variant_id: stringParam(),
    d_transaction_id: stringParam(),
    d_transaction_type: enumParam(SrSalesOrderType),
    d_customer_id: stringParam(),
    d_event_id: stringParam(),
    d_event_date: stringParam(),
    d_task_id: stringParam(),
    d_note_id: stringParam(),
});

export function AllDrawersProvider({ children }: { children?: ReactNode }) {
    const [, setQueryParams] = useQueryParams(drawerQueryParams);

    const openProductDetails = useCallback(
        (nextProductId: string, customerId?: string) => {
            setQueryParams(
                ({ d_depth = 0 }) => ({
                    d_depth: d_depth + 1,
                    d_customer_id: customerId,
                    d_product_variant_id: nextProductId,
                }),
                'push',
            );
        },
        [setQueryParams],
    );

    const openOrderQuoteDetails = useCallback(
        (type: SrSalesOrderType | `${SrSalesOrderType}`, id: string) => {
            setQueryParams(
                ({ d_depth = 0 }) => ({
                    d_depth: d_depth + 1,
                    d_transaction_type: type as SrSalesOrderType,
                    d_transaction_id: id,
                }),
                'push',
            );
        },
        [setQueryParams],
    );

    const openEventDetails = useCallback(
        (eventId: string, eventDate: string) => {
            setQueryParams(
                ({ d_depth = 0 }) => ({
                    d_depth: d_depth + 1,
                    d_event_id: eventId,
                    d_event_date: eventDate,
                }),
                'push',
            );
        },
        [setQueryParams],
    );

    const openTaskDetails = useCallback(
        (taskId: string) => {
            setQueryParams(
                ({ d_depth = 0 }) => ({
                    d_depth: d_depth + 1,
                    d_task_id: taskId,
                }),
                'push',
            );
        },
        [setQueryParams],
    );

    const openNoteDetails = useCallback(
        (noteId: string) => {
            setQueryParams(
                ({ d_depth = 0 }) => ({
                    d_depth: d_depth + 1,
                    d_note_id: noteId,
                }),
                'push',
            );
        },
        [setQueryParams],
    );

    const contextValue = useMemoDeepEqualValue<DrawersContextType>({
        openEventDetails,
        openNoteDetails,
        openOrderQuoteDetails,
        openProductDetails,
        openTaskDetails,
    });

    return <DrawersContext.Provider value={contextValue}>{children}</DrawersContext.Provider>;
}

export function AllDrawers({ canAddToDraft = false }: AllDrawersProps) {
    const navigate = useNavigate();
    const [queryParams, setQueryParams] = useQueryParams(drawerQueryParams);
    const { isInternal: lastPathIsPlato } = useReferrer();

    const depth = useRef(queryParams.d_depth ?? 0);
    depth.current = queryParams.d_depth ?? 0;

    const shouldDrawerOpen = Boolean(
        queryParams.d_product_variant_id ||
            (queryParams.d_transaction_id && queryParams.d_transaction_type) ||
            (queryParams.d_event_id && queryParams.d_event_date) ||
            queryParams.d_note_id ||
            queryParams.d_task_id,
    );

    const [isDrawerOpen, setDrawerOpen] = useState(shouldDrawerOpen);

    useEffect(() => {
        setDrawerOpen(shouldDrawerOpen);
    }, [shouldDrawerOpen, queryParams]);

    const closeDrawer = useCallback(() => {
        if (!lastPathIsPlato) {
            setQueryParams({}, 'replace');
        } else {
            navigate(-depth.current);
        }
        setDrawerOpen(false);
    }, [navigate, setQueryParams, lastPathIsPlato]);

    const goBack = depth.current > 1 ? () => navigate(-1) : undefined;

    return (
        <Drawer open={isDrawerOpen} onClose={closeDrawer}>
            {queryParams.d_product_variant_id && (
                <InventoryDetails
                    canAddToDraft={canAddToDraft}
                    customerId={queryParams.d_customer_id}
                    className="bg-neutral-50"
                    erpProductId={queryParams.d_product_variant_id}
                    onBack={goBack}
                    onClose={closeDrawer}
                />
            )}
            {queryParams.d_transaction_id && queryParams.d_transaction_type && (
                <QuoteOrderDetailPage
                    className="bg-neutral-50"
                    onBack={goBack}
                    onClose={closeDrawer}
                    transactionId={queryParams.d_transaction_id}
                    type={queryParams.d_transaction_type}
                />
            )}
            {queryParams.d_event_id && queryParams.d_event_date && (
                <EventDetails
                    date={queryParams.d_event_date}
                    eventId={queryParams.d_event_id}
                    onBack={goBack}
                    onClose={closeDrawer}
                />
            )}
            {queryParams.d_task_id && (
                <TaskDetails taskId={queryParams.d_task_id} onBack={goBack} onClose={closeDrawer} />
            )}
            {queryParams.d_note_id && (
                <NoteDetails noteId={queryParams.d_note_id} onBack={goBack} onClose={closeDrawer} />
            )}
        </Drawer>
    );
}

export function useDrawers(): DrawersContextType {
    const value = useContext(DrawersContext);
    if (!value) {
        throw new Error(`DrawersContext not found (did you forget to use the AllDrawers component?)`);
    }
    return value;
}
